import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { config } from "../configs/index";

export const proformaApi = createApi({
  reducerPath: "proformaApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${config.REACT_APP_BASE_URL}/api/proforma`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    login: builder.mutation<ApiResponse<ProfromaDataResponse>, ProformaLogin>({
      query: (body) => ({
        url: "proforma-login",
        method: "POST",
        body: body,
      }),
    }),
    getDefaultProformaValues: builder.query<
      ApiResponse<GetDefaultValues[]>,
      void
    >({
      query: () => ({
        url: "/get-proforma-values",
        method: "GET",
      }),
    }),
    existingProforma: builder.mutation<
      ApiResponse<ExistingProformaResponse>,
      any
    >({
      query: (body) => ({
        url: "/check-existing-proforma",
        method: "POST",
        body: body,
      }),
    }),
    confirmExistingProforma: builder.mutation<
      ApiResponse<ExistingProformaResponse>,
      any
    >({
      query: (body) => ({
        url: "/confirm-existing-proforma",
        method: "POST",
        body: body,
      }),
    }),
    getBaselineModal: builder.query<ApiResponse<GetBaselineModal>, string>({
      query: (name) => ({
        url: "/get-baseline-model",
        method: "GET",
        params: { name },
      }),
    })
    ,
    getContactGroup: builder.query<ApiResponse<IContactGroup[]>, void>({
      query: () => ({
        url: "/contact-group",
        method: "GET",
      }),
    }),
    sendProformaEmail: builder.mutation<ApiResponse<void>, ISendProformaEmail>({
      query: (body) => ({
        url: "/send-proforma-email",
        method: "POST",
        body: body,
      }),
    }),
    getStatesInfo: builder.query<StateInfoResponse, void>({
      query: () => ({
        url: `/statesInfo`,
      }),
    }),

    getAssumptionInputData: builder.query<ApiResponse<string[][]>, string>({
      query: (sheetId) => ({
        url: `/get-assumptions-data/${sheetId}`,
      }),
    }),

    createAmpVantageProforma: builder.mutation<IAmpVantageRes, IAmpVantageReq>({
      query: (body) => ({
        url: "/create-ampvantage",
        method: "POST",
        body,
      }),
    }),

    createMainProforma: builder.mutation<
      ApiResponse<IMainProformaRes>,
      IMainProformaReq
    >({
      query: (body) => ({
        url: "/create-proforma-copy",
        method: "POST",
        body,
      }),
    }),

    generateProforma: builder.mutation<IGenerateProformaRes, IGenerateProformaReq>({
      query: (body) => {
        const baseUrl = config.REACT_APP_PROFORMA_GENERATOR_MICROSERVICE_BASE_URL; 
        return {
          url: `${baseUrl}/api/proforma/generate`,
          method: "POST",
          body,
        };
      },
    }),

    generateProformaLegacy: builder.mutation<
      ApiResponse<{ jobId: string }>,
      IMainProformaReq
    >({
      query: (body) => ({
        url: "/generate",
        method: "POST",
        body,
      }),
    }),

    generateAmpVantageProforma: builder.mutation<
      ApiResponse<{ jobId: string }>,
      IAmpVantageReq
    >({
      query: (body) => ({
        url: "/generate/ampvantage",
        method: "POST",
        body,
      }),
    }),

    generateProformaPolling: builder.query<
      ApiResponse<
        | { position: null; folderId: string; errorStatus: null; proformaId?: string }
        | { position: number; folderId: null; errorStatus: null; proformaId?: string }
        | { position: null; folderId: null; errorStatus: number; proformaId?: string }
        | { position: null; folderId: null; errorStatus: null; proformaId: string }
      >,
      string
    >({
      query: (jobId) => ({
        url: "/generate/polling",
        method: "GET",
        params: {
          jobId,
        },
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useGetDefaultProformaValuesQuery,
  useGetBaselineModalQuery,
  useGetContactGroupQuery,
  useSendProformaEmailMutation,
  useGetStatesInfoQuery,
  useGetAssumptionInputDataQuery,
  useCreateAmpVantageProformaMutation,
  useCreateMainProformaMutation,
  useExistingProformaMutation,
  useConfirmExistingProformaMutation,
  useGenerateProformaMutation,
  useGenerateProformaLegacyMutation,
  useGenerateAmpVantageProformaMutation,
  useGenerateProformaPollingQuery,
  useLazyGenerateProformaPollingQuery,
} = proformaApi;
